import { api } from "@services/apiClient";

import { AffiliateConstants } from "@@types/IAffiliate";
import {
  fetchAffiliateAndUserDetails,
  fetchAffiliateDetails,
  fetchAffiliateInviteDetails,
  fetchAffiliateOfferDetails,
  fetchAffiliateProduct,
} from "@services/http/affiliates";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ManageAffiliateCommissionInput } from "@components/pages/product/Affiliates/components/AffiliatesList/components/ManageCommission/hooks/useManageAffiliateCommissionForm.types";
import { ManageCommissionsInput } from "@components/pages/product/Affiliates/components/ManageCommissions/hooks/useManageCommissionsForm.types";
export enum AffiliateQueriesKey {
  AFFILIATE_DETAILS = "/affiliate/:uuid/details",
  AFFILIATE_INVITE_DETAILS = "/affiliate/invite/",
  AFFILIATE_PRODUCT = "/affiliate/:uuid/product",
  AFFILIATE_OFFER_DETAILS = "/affiliates/offer-details/:uuid",
  AFFILIATE_AND_USER_DETAILS = "/affiliates/:uuid/details",
}

export function useFetchAffiliateProduct(uuid: string) {
  const query = useQuery([AffiliateQueriesKey.AFFILIATE_PRODUCT, uuid], () => fetchAffiliateProduct(uuid), {
    enabled: !!uuid,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  return {
    ...query,
    product: query.data,
  };
}

export function useFetchAffiliateDetails(uuid: string) {
  const query = useQuery([AffiliateQueriesKey.AFFILIATE_DETAILS, uuid], () => fetchAffiliateDetails(uuid), {
    enabled: !!uuid,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
    retry: 1,
  });

  return {
    ...query,
    product: query.data?.product,
  };
}

export function useFetchAffiliateInviteDetails(uuid: string) {
  const query = useQuery(
    [AffiliateQueriesKey.AFFILIATE_INVITE_DETAILS, uuid],
    () => fetchAffiliateInviteDetails(uuid),
    {
      enabled: !!uuid,
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: true,
      retry: 1,
    }
  );

  return {
    ...query,
    product: query.data?.product,
  };
}

export function useFetchAffiliateAndUserDetails(affiliateUuid: string, isModalOpen: boolean) {
  return useQuery(
    [AffiliateQueriesKey.AFFILIATE_AND_USER_DETAILS, affiliateUuid, isModalOpen],
    affiliateUuid ? () => fetchAffiliateAndUserDetails(affiliateUuid) : () => undefined,
    {
      enabled: !!affiliateUuid,
      staleTime: 1000 * 1, // 5 minutes
      refetchOnWindowFocus: true,
      retry: 1,
    }
  );
}

export function useFetchAffiliateOffersDetails(uuid: string) {
  return useQuery([AffiliateQueriesKey.AFFILIATE_OFFER_DETAILS, uuid], () => fetchAffiliateOfferDetails(uuid), {
    enabled: !!uuid,
    staleTime: 1000 * 10, // 10 seconds
    refetchOnWindowFocus: false,
  });
}

export interface UseChangeAffiliateCommissionData {
  payload: ManageAffiliateCommissionInput;
  affiliateUuid: string;
}

export function useChangeAffiliateCommission() {
  return useMutation(({ payload, affiliateUuid }: UseChangeAffiliateCommissionData) => {
    return api.put(
      `/affiliates/${affiliateUuid}/commission`,
      { ...payload },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  });
}

export interface UseChangeAffiliatesSettingsCommissionData {
  payload: ManageCommissionsInput;
  productUuid: string;
}

export function useChangeAffiliatesSettingsCommission() {
  return useMutation(({ payload, productUuid }: UseChangeAffiliatesSettingsCommissionData) => {
    return api.post(
      `/affiliates/default-commission`,
      { ...payload, productUuid },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  });
}

export interface UseCancelAffiliationProps {
  uuid: string;
  status: AffiliateConstants.Status;
  canceledBy: AffiliateConstants.CanceledBy;
}

export function useCancelAffiliation() {
  return useMutation((data: UseCancelAffiliationProps) => {
    return api.post(`/affiliates/update-status`, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  });
}

export interface UseChangeAffiliationStatusProps {
  affiliateUuid: string;
  status: AffiliateConstants.Status;
}

export function useChangeAffiliationStatus() {
  return useMutation((data: UseChangeAffiliationStatusProps) => {
    return api.post(`/affiliates/${data.affiliateUuid}/producer-update-status`, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  });
}
